import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiPlus, FiArrowLeft, FiTrash2, FiCheck } from 'react-icons/fi';
import { api } from '../api';
import { useMe } from '../App';
import { findPerm } from '../perm';

const ST = {
  open: { background: '#F3EFE7', color: '#8A7B5E' },
  reserved: { background: '#FDF3E3', color: '#A87B2D' },
  issued: { background: '#E9F0F7', color: '#3A6A96' },
  installed: { background: '#E9F3EC', color: '#3B7A4E' },
  cancelled: { background: '#FBEEEA', color: '#B4432E' },
};

export default function Requirements() {
  const me = useMe();
  const nav = useNavigate();
  const perm = findPerm(me.modules, 'requirements');
  const [rows, setRows] = useState(null);
  const [modal, setModal] = useState(false);
  const [toast, setToast] = useState('');

  async function load() { try { setRows(await api('/requirements')); } catch (e) { setToast(e.message); } }
  useEffect(() => { load(); }, []);
  function flash(m) { setToast(m); setTimeout(() => setToast(''), 2600); }

  return (
    <div>
      <div className="mod-head">
        <div className="titles">
          <div className="page-eyebrow">Inventory</div>
          <h1>Requirements</h1>
          <div className="count">{rows ? `${rows.length} requirement${rows.length !== 1 ? 's' : ''}` : 'Loading…'}</div>
        </div>
        {perm.can_create && <button className="btn btn-dark" onClick={() => setModal(true)}><FiPlus /> New requirement</button>}
      </div>

      <div className="table-card">
        {!rows ? <div className="empty">Loading…</div> :
         rows.length === 0 ? <div className="empty"><h3>No requirements yet</h3><p>Raise a requirement for a customer.</p></div> :
          <table className="data">
            <thead><tr>
              <th>Customer</th><th>Place / District</th><th>Required by</th><th>Aids</th><th>Status</th><th>Installed</th><th>Raised by</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => nav('/m/requirements/' + r.id)}>
                  <td style={{ fontWeight: 600 }}>{r.customer_name}
                    {r.source_campaign_name && <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--brass)', marginTop: 3 }}>from Campaign: {r.source_campaign_name}</div>}
                  </td>
                  <td style={{ color: 'var(--muted)' }}>{[r.place, r.district].filter(Boolean).join(', ') || '—'}</td>
                  <td style={{ color: 'var(--muted)' }}>{r.required_by ? r.required_by.slice(0, 10) : '—'}</td>
                  <td><span className="pill pill-muted">{r.item_count}</span></td>
                  <td><span className="pill" style={ST[r.status]}>{r.status}</span></td>
                  <td>{r.installed ? <span className="pill" style={{ background: '#E9F3EC', color: '#3B7A4E' }}>✓ {r.installed_date ? r.installed_date.slice(0, 10) : ''}</span> : '—'}</td>
                  <td style={{ color: 'var(--muted)' }}>{r.raiser_name}</td>
                </tr>
              ))}
            </tbody>
          </table>}
      </div>

      {modal && <RequirementModal onClose={() => setModal(false)} onSaved={() => { setModal(false); flash('Requirement created'); load(); }} />}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function RequirementModal({ onClose, onSaved }) {
  const [f, setF] = useState({ customer_name: '', place: '', district: '', required_by: '' });
  const [products, setProducts] = useState([]);
  const [items, setItems] = useState([{ product_id: '', qty: '' }]);
  const [error, setError] = useState(''); const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => { api('/store/options').then(setProducts).catch(() => {}); }, []);
  function setItem(i, k, v) { const n = items.slice(); n[i] = { ...n[i], [k]: v }; setItems(n); }
  function addItem() { setItems([...items, { product_id: '', qty: '' }]); }
  function removeItem(i) { setItems(items.filter((_, x) => x !== i)); }

  async function save() {
    setError(''); setBusy(true);
    const body = { ...f, items };
    try { await api('/requirements', { method: 'POST', body }); onSaved(); }
    catch (e) { setError(e.message); setBusy(false); }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <h2>New requirement</h2>
        <div className="field"><label>Customer name</label>
          <input value={f.customer_name} autoFocus onChange={set('customer_name')} placeholder="Customer / dealer name" /></div>
        <div className="grid2">
          <div className="field"><label>Place</label><input value={f.place} onChange={set('place')} placeholder="Town / area" /></div>
          <div className="field"><label>District</label><input value={f.district} onChange={set('district')} placeholder="District" /></div>
        </div>
        <div className="field"><label>Required by</label><input type="date" value={f.required_by} onChange={set('required_by')} /></div>

        <label style={{ fontSize: 12.5, fontWeight: 600, color: '#4A453E' }}>Marketing aids needed</label>
        <div className="targets-block">
          {items.map((it, i) => (
            <div className="target-row" key={i} style={{ gridTemplateColumns: '2fr 1fr auto' }}>
              <select value={it.product_id} onChange={(e) => setItem(i, 'product_id', e.target.value)}>
                <option value="">Select aid…</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <input type="number" placeholder="Qty" value={it.qty} onChange={(e) => setItem(i, 'qty', e.target.value)} />
              <button className="btn btn-sm btn-danger" onClick={() => removeItem(i)}><FiTrash2 /></button>
            </div>
          ))}
          <button className="btn btn-sm" onClick={addItem} style={{ marginTop: 4 }}><FiPlus /> Add aid</button>
        </div>

        {error && <div className="inline-error">{error}</div>}
        <div className="modal-actions">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-dark" onClick={save} disabled={busy || !f.customer_name.trim()}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// ---- Requirement detail: reserve / issue / bring-back / install ----
export function RequirementDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const me = useMe();
  const perm = findPerm(me.modules, 'store');
  const reqPerm = findPerm(me.modules, 'requirements');
  const [data, setData] = useState(null);
  const [action, setAction] = useState(null); // {item, kind}
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    try { setData(await api('/requirements/' + id)); } catch (e) { setToast(e.message); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  function flash(m) { setToast(m); setTimeout(() => setToast(''), 2600); }

  async function markInstalled() {
    const today = new Date().toISOString().slice(0, 10);
    try { await api(`/requirements/${id}/install`, { method: 'PATCH', body: { installed: true, installed_date: today } }); flash('Marked installed'); load(); }
    catch (e) { flash(e.message); }
  }

  if (!data) return <div className="empty">{toast || 'Loading…'}</div>;
  const r = data.requirement;

  return (
    <div>
      <button className="btn btn-sm" style={{ marginBottom: 14 }} onClick={() => nav('/m/requirements')}><FiArrowLeft /> All requirements</button>
      <div className="camp-summary">
        <div className="camp-main">
          <div className="page-eyebrow">Requirement</div>
          <h1 className="page-title" style={{ margin: '6px 0 8px' }}>{r.customer_name}</h1>
          <p className="page-desc" style={{ margin: 0 }}>
            {[r.place, r.district].filter(Boolean).join(', ')}{r.required_by ? ` · required by ${r.required_by.slice(0, 10)}` : ''}
          </p>
          <div style={{ marginTop: 12 }}>
            <span className="pill" style={ST[r.status]}>{r.status}</span>
            {r.installed && <span className="pill" style={{ background: '#E9F3EC', color: '#3B7A4E', marginLeft: 8 }}>Installed {r.installed_date ? r.installed_date.slice(0, 10) : ''}</span>}
          </div>
          {reqPerm.can_edit && !r.installed && (
            <button className="btn btn-sm" style={{ marginTop: 14 }} onClick={markInstalled}><FiCheck /> Mark installed on site</button>
          )}
        </div>
      </div>

      <div className="mod-head" style={{ marginTop: 24 }}><div className="titles"><h1 style={{ fontSize: 19 }}>Aids</h1></div></div>
      <div className="table-card">
        <table className="data">
          <thead><tr>
            <th>Product</th><th style={{ textAlign: 'right' }}>Needed</th><th style={{ textAlign: 'right' }}>Reserved</th>
            <th style={{ textAlign: 'right' }}>Issued</th><th style={{ textAlign: 'right' }}>Returned</th>
            <th style={{ textAlign: 'right' }}>Issuable stock</th>
            {perm.can_edit && <th style={{ textAlign: 'right' }}>Actions</th>}
          </tr></thead>
          <tbody>
            {data.items.map((it) => {
              const heldNotIssued = Number(it.reserved_qty) - Number(it.issued_qty);
              const outstanding = Number(it.issued_qty) - Number(it.returned_qty);
              return (
                <tr key={it.id}>
                  <td style={{ fontWeight: 600 }}>{it.product_name}</td>
                  <td style={{ textAlign: 'right' }}>{it.qty}</td>
                  <td style={{ textAlign: 'right' }}>{heldNotIssued > 0 ? <span className="pill" style={{ background: '#FDF3E3', color: '#A87B2D' }}>{heldNotIssued}</span> : 0}</td>
                  <td style={{ textAlign: 'right' }}>{outstanding > 0 ? <span className="pill" style={{ background: '#E9F0F7', color: '#3A6A96' }}>{outstanding}</span> : it.issued_qty}</td>
                  <td style={{ textAlign: 'right', color: 'var(--muted)' }}>{it.returned_qty}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600, color: it.issuable_stock <= 0 ? '#B4432E' : '#3B7A4E' }}>{it.issuable_stock}</td>
                  {perm.can_edit && (
                    <td><div className="row-actions" style={{ flexWrap: 'wrap' }}>
                      <button className="btn btn-sm" onClick={() => setAction({ item: it, kind: 'reserve' })}>Reserve</button>
                      {heldNotIssued > 0 && <button className="btn btn-sm" onClick={() => setAction({ item: it, kind: 'issue' })}>Issue</button>}
                      {heldNotIssued > 0 && <button className="btn btn-sm btn-danger" onClick={() => setAction({ item: it, kind: 'reserve_release' })}>Release</button>}
                      {outstanding > 0 && <button className="btn btn-sm btn-danger" onClick={() => setAction({ item: it, kind: 'issue_return' })}>Return</button>}
                    </div></td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {action && <ActionModal {...action} onClose={() => setAction(null)} onDone={(m) => { setAction(null); flash(m); load(); }} />}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function ActionModal({ item, kind, onClose, onDone }) {
  const [qty, setQty] = useState(''); const [reason, setReason] = useState('');
  const [error, setError] = useState(''); const [busy, setBusy] = useState(false);
  const titles = { reserve: 'Reserve stock', issue: 'Issue stock', reserve_release: 'Release reserved (bring back)', issue_return: 'Return issued to store' };
  const needsReason = kind === 'reserve_release' || kind === 'issue_return';

  async function go() {
    setError(''); setBusy(true);
    try {
      if (kind === 'reserve') await api(`/requirements/items/${item.id}/reserve`, { method: 'POST', body: { qty } });
      else if (kind === 'issue') await api(`/requirements/items/${item.id}/issue`, { method: 'POST', body: { qty } });
      else await api(`/requirements/items/${item.id}/bringback`, { method: 'POST', body: { qty, kind, reason } });
      onDone(titles[kind] + ' done');
    } catch (e) { setError(e.message); setBusy(false); }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{titles[kind]}</h2>
        <p className="page-desc" style={{ margin: '0 0 14px', fontSize: 13 }}>{item.product_name} · issuable {item.issuable_stock}</p>
        <div className="field"><label>Quantity</label>
          <input type="number" value={qty} autoFocus onChange={(e) => setQty(e.target.value)} placeholder="0" /></div>
        {needsReason && <div className="field"><label>Reason / note (logged for reminder)</label>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this being brought back?" /></div>}
        {error && <div className="inline-error">{error}</div>}
        <div className="modal-actions">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-dark" onClick={go} disabled={busy || !qty}>{busy ? 'Working…' : 'Confirm'}</button>
        </div>
      </div>
    </div>
  );
}
